/**
 * Persona Forge — locked-template enforcement.
 *
 * A locked persona's Style Prompt must survive every regeneration
 * unchanged except for BPM and key. Studio Team's LLM pipeline is
 * instructed to preserve persona text (same "preserve this" pattern as
 * production architecture), but instruction alone is a request, not a
 * guarantee -- LLMs are unreliable at "reproduce this exact string, but
 * substitute exactly these two tokens." The Style block is a single,
 * cleanly-isolated string (Block 2), so it's the one field this can
 * actually be made deterministic for: after generation, this module
 * throws away whatever RIFF/FADER wrote for Style and replaces it with
 * the persona's locked template, substituting {bpm}/{key} in code.
 *
 * Vocal Physics (Layer 1) and Rhythm Section Physics (Layer 2) are NOT
 * substituted here -- they live embedded inside the LLM-generated Lyrics
 * block (Character Dossier, arrangement cues, alongside actual lyric
 * content), not as an isolated field, so there's no clean seam to swap
 * them into deterministically. Those are enforced the same way
 * productionArchitecture already is: as strong constraint text in the
 * brief sent to Studio Team (see callers in App.tsx), which is real
 * influence but not a hard guarantee.
 */

export interface LockedPersonaFields {
  useLockedTemplate: boolean;
  styleTemplate: string;
  vocalPhysicsLocked: string;
  rhythmPhysicsGuidance: string;
  defaultBpm: number | null;
  defaultKey: string;
}

/**
 * The soft half of persona locking -- prompt-constraint text appended to a
 * Studio Team brief, same "preserve this" pattern as production
 * architecture. Shared between the client (StudioTeamPanel) and the two
 * server-side executors that run Studio Team generation outside the
 * browser (Roulette/bulk runs, Album Mode) so the wording never drifts
 * between the three call sites.
 */
export function buildPersonaLockConstraintText(
  persona: (LockedPersonaFields & { name: string }) | null | undefined,
  overrides: StyleOverrides = {}
): string {
  if (!persona?.useLockedTemplate) return '';
  const parts: string[] = [];
  if (persona.styleTemplate.trim()) {
    // Rendering with the persona's *default* bpm/key here (the old
    // behavior) stamps a specific tempo/key into a "reproduce exactly"
    // instruction -- which directly contradicts whatever tempo/key this
    // song's own brief/Riff/Phi settle on, and that contradiction is the
    // likely cause of Pat dropping Block 2 outright rather than resolving
    // it (see incident log, Forgejo #87/#91). When the caller doesn't yet
    // know this song's real bpm/key, send the literal {bpm}/{key} tokens
    // instead and say plainly that those two are the song's own to set.
    const hasOverrides = overrides.bpm !== undefined || overrides.key !== undefined;
    const rendered = hasOverrides ? renderLockedStyleTemplate(persona, overrides) : persona.styleTemplate;
    parts.push(
      `STYLE PROMPT (locked persona "${persona.name}" — reproduce every other word exactly, in this exact order; ` +
      `do not alter, paraphrase, or invent a different sonic direction). The placeholder tokens \`{bpm}\` and ` +
      `\`{key}\` are the one exception: replace them with THIS song's own tempo and key as Riff/Phi determine ` +
      `below -- never the persona's saved defaults:`,
      rendered,
    );
  }
  if (persona.vocalPhysicsLocked.trim()) {
    parts.push(
      'VOCAL PHYSICS (Layer 1 — MUST NOT CHANGE, reproduce this delivery exactly):',
      persona.vocalPhysicsLocked,
    );
  }
  if (persona.rhythmPhysicsGuidance.trim()) {
    parts.push(
      'RHYTHM SECTION PHYSICS (Layer 2 — changeable within the genre, but do not contradict this direction):',
      persona.rhythmPhysicsGuidance,
    );
  }
  return parts.join('\n\n');
}

export interface StyleOverrides {
  bpm?: number | string;
  key?: string;
}

/**
 * Substitutes {bpm} and {key} placeholders in a locked Style template.
 * Falls back to the persona's defaults when an explicit override isn't
 * given, then to the literal placeholder text if neither is set (so a
 * missing value is visibly wrong in the output rather than silently
 * dropped).
 */
export function renderLockedStyleTemplate(
  persona: Pick<LockedPersonaFields, 'styleTemplate' | 'defaultBpm' | 'defaultKey'>,
  overrides: StyleOverrides = {}
): string {
  const bpm = overrides.bpm ?? persona.defaultBpm ?? '{bpm}';
  const key = overrides.key || persona.defaultKey || '{key}';
  return persona.styleTemplate
    .replaceAll('{bpm}', String(bpm))
    .replaceAll('{key}', String(key));
}

/**
 * Best-effort pull of the bpm/key this specific song actually used out of
 * Fader's own (pre-override) Style text, for use as `applyLockedPersonaStyle`
 * overrides. Without this, the post-generation Style substitution always
 * falls back to the persona's static defaultBpm/defaultKey regardless of
 * what Riff/Phi actually locked the song to -- defeating the "everything is
 * locked except bpm and key" premise this feature exists for (Forgejo #91).
 * Returns an empty object (falls through to persona defaults) when nothing
 * matches -- never worse than the old always-default behavior.
 */
export function extractSongBpmKey(styleText: string): StyleOverrides {
  const bpmMatch = styleText.match(/\b(\d{2,3})\s*bpm\b/i);
  const keyMatch = styleText.match(/\b([A-G](?:#|b)?)\s*(?:minor|major|min|maj)\b/i);
  const overrides: StyleOverrides = {};
  if (bpmMatch) overrides.bpm = Number(bpmMatch[1]);
  if (keyMatch) overrides.key = keyMatch[1];
  return overrides;
}

/**
 * Applies a locked persona's Style override to a parsed Studio Team result.
 * No-op (returns blocks unchanged) when the persona isn't in locked-template
 * mode or has no styleTemplate saved -- callers can apply this unconditionally
 * without checking persona state first.
 */
export function applyLockedPersonaStyle<T extends { style: string }>(
  blocks: T,
  persona: LockedPersonaFields | null | undefined,
  overrides: StyleOverrides = {}
): T {
  if (!persona?.useLockedTemplate || !persona.styleTemplate.trim()) {
    return blocks;
  }
  return { ...blocks, style: renderLockedStyleTemplate(persona, overrides) };
}
